// Core components
import { AfterViewInit, Component, Input, OnDestroy, OnInit } from "@angular/core";
import { isArray } from "util";

// Third party components
import { MatDialog } from "@angular/material";
import { FieldType } from "@ngx-formly/material";
import { TranslateService } from "@ngx-translate/core";
import { MatProgressButtonOptions } from "mat-progress-buttons";
import { FileSystemFileEntry, NgxFileDropEntry } from "ngx-file-drop";
import { ToastrService } from "ngx-toastr";
import { Subscription } from "rxjs";
import { finalize } from "rxjs/operators";

// Custom components
import { ConfigService } from 'src/app/shared/services/config.service';
import { FileService } from "src/app/shared/services/file.service";
import { HelperService } from "src/app/shared/services/helper.service";
import { LightboxDialogComponent } from "../../dialogs/lightbox-dialog/lightbox-dialog.component";

@Component({
  selector: "app-formly-file-uploader-structure-type",
  templateUrl: "./file-uploader-structure-type.component.html",
  styleUrls: ["./file-uploader-structure-type.component.scss"]
})
export class FileUploaderStructureTypeComponent extends FieldType
  implements OnInit, OnDestroy, AfterViewInit {
  private subscriptions: Subscription[] = [];
  triggerFileUpload = false;
  triggerFileReset = false;
  importFormSubmitBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant("common.cta.import"),
    spinnerSize: 19,
    raised: true,
    stroked: true,
    flat: false,
    fab: false,
    buttonColor: "primary",
    spinnerColor: "primary",
    fullWidth: false,
    disabled: false,
    mode: "indeterminate",
    customClass: "mt-2"
  };
  uploadedFiles: string[] = [];
  formField;
  formControl;
  @Input() customLabel;
  filesToUpload: any[];
  previousValue: any;
  imageLoader = false;

  constructor(
    private helperService: HelperService,
    private translateService: TranslateService,
    private toastrService: ToastrService,
    private fileService: FileService,
    private configService: ConfigService,
    public dialog: MatDialog
  ) {
    super();
  }

  /**
   * Init component
   *
   * @since 1.0.0
   */
  ngOnInit(): void {
    super.ngOnInit();

    // Set default tempateOptions
    if (typeof this.to.allowedFilesNumber === "undefined") {
      this.to.allowedFilesNumber = 1;
    }
    if (typeof this.to.allowedFilesExtensions === "undefined") {
      this.to.allowedFilesExtensions = "*";
    }
    if (typeof this.to.defaultValue === "undefined") {
      this.to.defaultValue = [];
    }

    // Set initial values
    this.filesToUpload = this.to.defaultValue;

    if (this.formControl.value
      && this.previousValue !== this.formControl.value) {
      this.previousValue = JSON.parse(JSON.stringify(this.formControl.value));
      this.initFiles(this.formControl.value);
    }

    // Handle form value changes (these are the uploaded files)
    this.subscriptions.push(
      this.formControl.valueChanges.subscribe(res => {
        if (res === this.previousValue) {
          return;
        }
        this.filesToUpload = [];
        this.previousValue = JSON.parse(JSON.stringify(res));
        this.initFiles(res);
      })
    );
  }

  /**
   * After view init logic
   *
   * @since 1.0.0
   */
  ngAfterViewInit(): void {
    super.ngAfterViewInit();
  }

  /**
   * Handle component destroy
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  /**
   * Handle event "uploadFiles" emitted from file-uploader component
   *
   * @since 1.0.0
   *
   * @param files Files to upload
   * @returns undefined
   */
  onUploadFiles(files: NgxFileDropEntry[]): void {
    if (this.options.formState.disabled) {
      console.warn('Block upload because field is disabled') //@todo: translate
      return;
    } else {
      this.importFormSubmitBtnOptions.disabled = false;
    }

    // Check total file number (if too much don't even try to upload anything)
    const currentFilesNumber = this.uploadedFiles.length + this.filesToUpload.length;
    if (currentFilesNumber >= this.to.allowedFilesNumber) {
      this.toastrService.warning(this.translateService.instant("common.errors.maxElementsReached"))
      return;
    }

    const currentFilesNames = [...this.uploadedFiles, ...this.filesToUpload].map(file => file.originalName);

    this.importFormSubmitBtnOptions.active = true;

    // You could upload it like this:
    const formData = new FormData();

    let counter = 1;
    for (const givenFile of files) {
      const fileEntry = givenFile.fileEntry as FileSystemFileEntry;
      if (currentFilesNames.includes(fileEntry.name)) {
        this.toastrService.warning(this.translateService.instant("common.errors.elementExists"));
        return;
      }
      fileEntry.file((file: File) => {
        const name = `file[]`;
        formData.append(name, file, givenFile.relativePath);
        counter++;
        if (counter === files.length + 1) {
          const uploadFilesSubscription = this.fileService
            .uploadImportingFiles(formData)
            .pipe(
              finalize(() => (this.importFormSubmitBtnOptions.active = false))
            )
            .subscribe(
              res => {
                // Accept all-and-only last (this.to.allowedFilesNumber)
                // files (this is needed! do not remove!)
                let files = [];
                if (res.status) {
                  files = res.files.slice(-this.to.allowedFilesNumber);
                }

                // If available, call the callback
                if (typeof this.to.callback === "function") {
                  this.to.callback(res, this.model);
                }

                // Handle res status
                if (res.status) {
                  this.triggerFileReset = !this.triggerFileReset; // Reset drop-zone area
                  this.updateUpdatedFiles(files);
                  return;
                }
                if (res.message) {
                  this.toastrService.error(
                    this.translateService.instant("common.words.warning"),
                    res.message
                  );
                  return;
                }
                this.toastrService.error(
                  this.translateService.instant("common.errors.unknown")
                );
              },
              err => {
                // Choose one of the following error handling
                // method. The first one show a message right
                // under the form fields (if the form is properly
                // setted), the second one show toastr
                // notifications for each error
                // this.helperService.handleFormError(this.form, err);
                this.helperService.handleError(err);
              }
            );
          this.subscriptions.push(uploadFilesSubscription);
        }
      });
    }
  }

  /**
   * Init element uploaded files
   *
   * @since 1.0.0
   */
  initFiles(files: any[]): void {
    const apiHost = this.configService.apiHost;
    // Update image previews list
    this.uploadedFiles = [];
    if (files) {
      files.forEach((file: any) => {
        const path = file.path;
        // if (path.startsWith("public")) {
        if (path.substring(0,apiHost.length) !== apiHost) {
          const newPath = `${apiHost}\\${path}`;
          file.path = newPath;
        }
        // }
        this.uploadedFiles.push(file);
      });
    } else {
      this.uploadedFiles = [];
    }
  }

  /**
   * Current files to upload
   *
   * @since 1.0.0
   */
  updateUpdatedFiles(filesArray: any[]/*, setValue: boolean*/): void {
    let files = filesArray;
    // if (typeof setValue === "undefined") {
    //   setValue = false;
    // }
    const apiHost = this.configService.apiHost;
    // Update image previews list
    if (files) {
      files.forEach((file: any) => {
        const path = file.path;
        // if (path.stkartsWith("public")) {
        const newPath = `${apiHost}\\${path}`;
        file.path = newPath;
        // }
        if (path.includes("temp")) {
          this.filesToUpload.push(file);
          // Prevent visualization of more previews than allowed
          this.filesToUpload = this.filesToUpload.slice(-this.to.allowedFilesNumber);
        } else {
          // this.uploadedFiles.push(file);
        }
      });
    } else {
      this.filesToUpload = [];
    }

    // Set value in model/form
    if (this.model.files && Array.isArray(this.model.files)) {
      // here we're merging previous uploaded files with new ones
      this.filesToUpload.forEach((file: any) => {
        this.model.files.push(file);
      });
    }
    if (Array.isArray(this.value)) {
      // here we're merging previous uploaded files with new ones
      this.filesToUpload.forEach((file: any) => {
        this.value.push(file);
      });
    }
  }

  /**
   * Remove given item from:
   * 1) File previews
   * 2) Field's value
   *
   * @since 1.0.0
   */
  onRemove(file: any): void {
    // Remove file from previews
    this.filesToUpload = this.filesToUpload.filter(
      (item: any) => item._id !== file._id
    );

    const key = this.key as string;
    // Remove file from field value
    if (typeof file._id !== "undefined" && this.model[key] === file._id) {
      this.model[key] = null;
    }
    if (isArray(this.model[key])) {
      this.model[key] = this.model[key].filter(
        (item: any) => item._id !== file._id
      );
    }
  }

  /**
   * Remove given item from uploaded files
   *
   * @since 1.0.0
   */
  onRemoveUploadedFile(file: any): void {
    // Remove file from previews
    this.uploadedFiles = this.uploadedFiles.filter(
      (item: any) => item._id !== file._id
    );

    const key = this.key as string;
    // Remove file from model/form
    if (typeof file._id !== "undefined" && this.model[key] === file._id) {
      this.model[key] = null;
    }
    if (isArray(this.model[key])) {
      this.model[key] = this.model[key].filter(
        (item: any) => item._id !== file._id
      );
    }

    if (Array.isArray(this.value)) {
      this.value.splice(this.value.indexOf(file), 1);
    }
  }

  /**
   * Handle click on a image preview and opens it in a dialog
   *
   * @since 1.0.0
   */
  onOpenImage(file: any): void {
    this.dialog.open(LightboxDialogComponent, {
      data: {
        url: file.path,
        createdAt: file.createdAt
      },
      panelClass: "lightbox-dialog"
    });
  }
}
